import axiosInterceptor from '../utils/axios/axiosInterceptor';
import type { ProjectProvisionStatus } from '../types/onboarding';

const api = () => axiosInterceptor(true);

/**
 * Run-scoped project API base. Most dashboard data lives under
 * `/project/{project_id}/runs/{run_id}/...`.
 */
export function projectRunBasePath(
  projectId: number,
  runId: number | null | undefined
): string | null {
  if (!projectId || projectId <= 0 || runId == null || runId <= 0) {
    return null;
  }
  return `/project/${projectId}/runs/${runId}`;
}

export type ProjectRunStatus = 'IN_PROGRESS' | 'COMPLETED' | 'FAILED';

export interface ProjectRunRow {
  run_id: number;
  run_number: number | null;
  project_id: number;
  status: ProjectRunStatus;
  metadata: Record<string, unknown> | null;
  created_at: string | null;
  completed_at: string | null;
  created_by_name?: string | null;
}

export interface ListProjectRunsResponse {
  runs: ProjectRunRow[];
  page_number?: number;
  page_size?: number;
  total_count?: number;
  in_progress_count?: number;
  has_more?: boolean;
  provision_status?: ProjectProvisionStatus;
  provision_error?: string;
}

export async function listProjectRuns(
  projectId: number,
  params?: {
    pageNumber?: number;
    pageSize?: number;
    signal?: AbortSignal;
  }
): Promise<ListProjectRunsResponse> {
  const response = await api().get<ListProjectRunsResponse>(
    `/project/${projectId}/runs`,
    {
      signal: params?.signal,
      params:
        params?.pageNumber !== undefined || params?.pageSize !== undefined
          ? {
              page_number: params?.pageNumber ?? 0,
              page_size: params?.pageSize ?? 25,
            }
          : {
              page_number: 0,
              page_size: 25,
            },
    }
  );
  return response.data;
}

export async function getProjectRun(
  projectId: number,
  runId: number
): Promise<ProjectRunRow> {
  const response = await api().get<ProjectRunRow>(
    `/project/${projectId}/runs/${runId}`
  );
  return response.data;
}

/** Body for POST /project/{project_id}/runs (optional overrides; server merges from project PROJECT_CONFIG template). */
export interface CreateProjectRunRequest {
  scaling_factor?: string | number;
  db_type?: string;
  host?: string;
  spec?: string;
}

export interface CreateProjectRunResponse {
  run_id: number;
  status: string;
  metadata: Record<string, unknown>;
  schema_prefix: string;
  query_log_prefix: string;
}

export async function createProjectRun(
  projectId: number,
  body: CreateProjectRunRequest
): Promise<CreateProjectRunResponse> {
  const response = await api().post<CreateProjectRunResponse>(
    `/project/${projectId}/runs`,
    body
  );
  return response.data;
}

/**
 * GET step-status map for a run (same shape as dashboard).
 *
 * @throws Axios error on non-2xx responses, including 404 when RUN_STEP_STATUS
 *   was never initialized for the run. Does not swallow 404 as an empty map.
 * @see ProjectRunsModal for an example caller that handles 404 in try/catch.
 */
export async function fetchRunStepStatus(
  projectId: number,
  runId: number
): Promise<Record<string, unknown>> {
  const base = projectRunBasePath(projectId, runId);
  if (!base) {
    return {};
  }
  const response = await api().get<{
    run_step_status?: Record<string, unknown>;
  }>(`${base}/step-status`);
  return response.data.run_step_status ?? {};
}

export async function completeProjectRun(
  projectId: number,
  runId: number
): Promise<void> {
  await api().put(`${projectRunBasePath(projectId, runId)}/complete`, {});
}
